import React, { useMemo } from 'react';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import { EstateUnit, EstateUnitStatusEnum } from '../../../types/EstateUnit';
import EstateUnitCard from './EstateUnitCard';
import {
  CardsStack,
  EmptyUnitsMessage,
  InactiveSectionTitle,
  Panel,
  PanelTitle,
  SectionDivider
} from '../styles';

type EstateUnitsPanelProps = {
  units: EstateUnit[];
  isLoading: boolean;
  selectedUnitId: string | null;
  canManage: boolean;
  onSelectUnit: (unit: EstateUnit) => void;
  onEditUnit: (unit: EstateUnit) => void;
  onToggleUnitStatus: (unit: EstateUnit, action: 'ACTIVE' | 'INACTIVE') => void;
};

const EstateUnitsPanel: React.FC<EstateUnitsPanelProps> = ({
  units,
  isLoading,
  selectedUnitId,
  canManage,
  onSelectUnit,
  onEditUnit,
  onToggleUnitStatus
}) => {
  const { activeUnits, inactiveUnits } = useMemo(() => {
    const active: EstateUnit[] = [];
    const inactive: EstateUnit[] = [];

    units.forEach(unit => {
      if (unit.status === EstateUnitStatusEnum.active) {
        active.push(unit);
      } else {
        inactive.push(unit);
      }
    });

    return { activeUnits: active, inactiveUnits: inactive };
  }, [units]);

  return (
    <Panel>
      <Dimmer active={isLoading} inverted>
        <Loader />
      </Dimmer>

      <PanelTitle>
        <Icon name="map marker alternate" />
        Unidades ativas
      </PanelTitle>

      {activeUnits.length === 0 && !isLoading ? (
        <EmptyUnitsMessage>Nenhuma unidade ativa encontrada.</EmptyUnitsMessage>
      ) : (
        <CardsStack>
          {activeUnits.map(unit => (
            <EstateUnitCard
              key={unit.id}
              unit={unit}
              isSelected={selectedUnitId === unit.id}
              isSelectable
              canManage={canManage}
              onSelect={onSelectUnit}
              onEdit={onEditUnit}
              onToggleStatus={onToggleUnitStatus}
            />
          ))}
        </CardsStack>
      )}

      {inactiveUnits.length > 0 && (
        <>
          <SectionDivider />
          <InactiveSectionTitle>
            <Icon name="ban" />
            Unidades inativas
          </InactiveSectionTitle>
          <CardsStack>
            {inactiveUnits.map(unit => (
              <EstateUnitCard
                key={unit.id}
                unit={unit}
                isSelectable={false}
                canManage={canManage}
                onEdit={onEditUnit}
                onToggleStatus={onToggleUnitStatus}
              />
            ))}
          </CardsStack>
        </>
      )}
    </Panel>
  );
};

export default EstateUnitsPanel;
